import React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

interface OrderSuccessProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Protected route for order success page.
 * Allows access if:
 * 1. There's a valid orderSuccess flag in sessionStorage (account payment)
 * 2. URL has orderId or orderNumber parameter (Worldpay redirect)
 * 
 * If neither condition is met, redirects to home page.
 */
const OrderSuccessProtectedRoute: React.FC<OrderSuccessProtectedRouteProps> = ({ children }) => {
  const [searchParams] = useSearchParams();
  
  // Check URL params first (for Worldpay redirects)
  const orderIdFromUrl = searchParams.get('orderId');
  const orderNumberFromUrl = searchParams.get('orderNumber');
  const hasValidUrlParams = !!(orderIdFromUrl || orderNumberFromUrl);

  const orderSuccessState = React.useMemo(() => {
    try {
      const stored = sessionStorage.getItem('orderSuccess');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.orderNumber) {
          return parsed;
        }
      }
    } catch (error) {
      // Invalid JSON, remove it
      sessionStorage.removeItem('orderSuccess');
    }
    return null;
  }, []);

  // Allow access if we have URL params (Worldpay redirect) or sessionStorage (account payment)
  if (!orderSuccessState && !hasValidUrlParams) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default OrderSuccessProtectedRoute;

